import { H4, Paragraph, View, YStack, XStack, Separator, useEvent } from 'tamagui'
import * as Components from '../../components'
import * as E2E from '../../e2e'
import * as Stores from '../../stores'
import { MainWrapper } from '@/src/common/wrappers/MainWrapper'
import { DesignSystem } from '@/src/design-system'
import { Switch } from '@/src/common/components/Switch'
import { Fragment, useEffect, useState } from 'react'
import { Button } from '@/src/common/components/Button'
import { pickFile, type PickedFile } from './pickFile'
import { routerBack } from '@/src/common/utils/routerBack'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { useMediaQuery } from 'react-responsive'
import { triggerSegmentAction } from '@/src/services/segment'

export function EarnOnMortgageDocumentUpload() {
  const { data: awardQueryResult } = Stores.MortgageEarnApi.endpoints[
    'GET /mortgage/earn'
  ].useQuery({})
  const [getUploadLink] =
    Stores.MortgageEarnApi.endpoints['POST /mortgage/earn/:earnId/upload'].useMutation()
  const [markDocumentUploaded] =
    Stores.MortgageEarnApi.endpoints[
      'POST /mortgage/earn/:earnId/uploads/:uploadId/complete'
    ].useMutation()
  const Alert = useAlert('Mesa Home')
  const [isAgreed, setIsAgreed] = useState(false)
  const [pickedFiles, setPickedFiles] = useState<PickedFile[]>([])
  const canSubmit = pickedFiles.length > 0 && isAgreed
  const [isUploading, setIsUploading] = useState(false)
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0)
  const isLargeScreen = useMediaQuery({ minWidth: 768 })

  const handleAddFile = useEvent(async () => {
    const file = await pickFile({ fileTypes: ['application/pdf'] })
    if (file) {
      void triggerSegmentAction({
        component: 'Button',
        action: 'MesaHome MortgagePayment SelectDocumentToUpload',
      })
      setPickedFiles((prev) => [...prev, file])
    }
  })

  const handleDeleteFile = useEvent((file: PickedFile) => {
    setPickedFiles((prev) => prev.filter((f) => f.id !== file.id))
  })

  const handlePerformUpload = useEvent((file: PickedFile) => async () => {
    if (!awardQueryResult?.latestMonthlyAward.id) {
      return false
    }

    const { data, error } = await getUploadLink({
      params: { earnId: awardQueryResult.latestMonthlyAward.id },
      body: {
        fileName: file.name,
        fileType: 'PDF',
      },
    })

    if (error || !data) {
      throw error ?? new Error('Failed to get upload link')
    }

    const uploadResponse = await fetch(data.url, {
      method: 'PUT',
      body: file.data,
      headers: {
        'Content-Type': file.mimeType,
      },
    })

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload document', { cause: { uploadResponse } })
    }

    const { error: markDocumentUploadedError } = await markDocumentUploaded({
      params: { earnId: awardQueryResult.latestMonthlyAward.id, uploadId: data.id },
    })

    if (markDocumentUploadedError) {
      throw markDocumentUploadedError
    }

    return true
  })

  const handleUploadError = useEvent(() => {
    Alert.alert(
      'We had a problem uploading your document. Please try again or contact support@mesamember.com',
    )
    void routerBack()
  })

  const handleUploadComplete = useEvent(() => {
    setCurrentDocumentIndex((prev) => prev + 1)
  })

  useEffect(() => {
    if (isUploading && currentDocumentIndex === pickedFiles.length) {
      setIsUploading(false)
      void routerBack()
    }
  }, [currentDocumentIndex, isUploading, pickedFiles.length])

  if (!awardQueryResult) {
    return <DesignSystem.Loading.Screen />
  }

  return (
    <MainWrapper
      screen="/main/home/earn-on-mortgage-document-upload"
      testID={E2E.Config.Ids.View.EarnOnMortgageDocumentUpload}
      showProfile={false}
      headerSectionProps={{
        backLinkProps: { href: '/main/home/earn-on-mortgage', dismissTo: true },
        backProps: { testID: E2E.Config.Ids.Press.IndexBack },
        style: { marginBottom: 0 },
        title: 'Mesa Home',
      }}
    >
      <DesignSystem.Card style={{ flex: 1, padding: 0, paddingBottom: 8, marginBottom: 16 }}>
        <YStack flex={1} gap="$4" px="$2" py="$4">
          <H4>You're almost done!</H4>
          <Paragraph>
            Please upload your most recent mortgage statement (dated within the last 60 days) to
            verify your monthly payment.
          </Paragraph>
          <View flex={1} flexDirection={isLargeScreen ? 'row' : 'column'} gap="$4">
            <YStack
              order={isLargeScreen ? 2 : 0}
              flex={1}
              minW={0}
              flexBasis={isLargeScreen ? 0 : 'auto'}
            >
              <Paragraph>Your statement must include:</Paragraph>
              <Paragraph ml="$2">&bull; Mortgage owner name</Paragraph>
              <Paragraph ml="$2">&bull; Property address</Paragraph>
              <Paragraph ml="$2">&bull; Loan details</Paragraph>
              <Paragraph ml="$2">&bull; Monthly payment amount</Paragraph>
              <Paragraph ml="$2">&bull; Statement dates</Paragraph>
            </YStack>
            <Separator order={1} self="stretch" vertical={isLargeScreen} />
            <YStack
              order={isLargeScreen ? 0 : 2}
              flex={1}
              minW={0}
              flexBasis={isLargeScreen ? 0 : 'auto'}
              gap="$4"
            >
              <Paragraph>Accepted file type: PDF</Paragraph>
              <Button
                title="Add Document"
                variant="outlined"
                disabled={isUploading}
                onPress={() => void handleAddFile()}
                testID={E2E.Config.Ids.Press.AddDocument}
              />
              {pickedFiles.length > 0 && (
                <YStack gap="$2" my="$4">
                  {pickedFiles.map((file, index) => (
                    <Fragment key={file.name}>
                      <Separator />
                      <Components.DocumentToUpload
                        fileName={file.name}
                        shouldUpload={isUploading && currentDocumentIndex === index}
                        performUpload={handlePerformUpload(file)}
                        onUploadError={handleUploadError}
                        onUploadComplete={handleUploadComplete}
                        onDeletePress={() => handleDeleteFile(file)}
                        disabled={isUploading}
                      />
                    </Fragment>
                  ))}
                </YStack>
              )}
            </YStack>
          </View>
          <Separator />
          <View width={isLargeScreen ? '50%' : '100%'} self="center">
            <Switch
              checked={isAgreed}
              segmentAction="MesaHome MortgagePayment AgreeToTermsAndConditions"
              segmentComponent="Switch"
              onCheckedChange={setIsAgreed}
              label={{
                render: ({ switchComponent }) => (
                  <XStack gap="$2" items="flex-start" maxW="100%">
                    <View mt="$2">{switchComponent}</View>
                    <Paragraph shrink={1} textWrap="wrap">
                      By submitting this form, you agree that: (1) Mesa is able to view your
                      statement which is required to earn mortgage rewards; (2) Mesa Mortgage may
                      review your mortgage data to check for better rates and refinancing options;
                      (3) Mesa Mortgage may contact you about mortgage rates or other mortgage
                      offerings; (4) this rate check service is free of charge.
                    </Paragraph>
                  </XStack>
                ),
              }}
            />
          </View>
          <Button
            title="Upload and Submit"
            disabled={!canSubmit || isUploading}
            loading={isUploading}
            segmentAction="MesaHome MortgagePayment SubmitDocuments"
            segmentComponent="Button"
            onPress={() => {
              setIsUploading(true)
            }}
            testID={E2E.Config.Ids.Press.UploadDocument}
          />
        </YStack>
      </DesignSystem.Card>
    </MainWrapper>
  )
}
